import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  getDeliveryTracking,
  syncOrderTracking,
  getDeliveryCompanies,
  syncTrackingStatuses,
  getSyncJobStatus,
  getConfirmationAgents,
} from "../services/api";
import { normalizePhone } from "../utils/phoneUtils";
import {
  Search,
  ChevronRight,
  Package,
  Clock,
  Calendar,
  RefreshCw,
  AlertCircle,
  XCircle,
  Eye,
  TrendingUp,
  Phone,
  Truck,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import DuplicateBadge from "../components/DuplicateBadge";
import TrackingTimeline from "../components/TrackingTimeline";
import { formatSize } from "../utils/variantUtils";

const DeliveryTracking = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAgent = user?.role === "confirmation";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [syncingId, setSyncingId] = useState(null);
  const [syncJob, setSyncJob] = useState(null); // { jobId, status, processed, total }
  const [syncToasts, setSyncToasts] = useState([]);
  const syncPollRef = useRef(null);

  // Filter states
  const [filters, setFilters] = useState({
    providerSlug: "",
    label: "",
    search: "",
    from: "",
    to: "",
    source: "",
    confirmedBy: "",
  });

  // Modal state for timeline
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [providers, setProviders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showTimeline, setShowTimeline] = useState(false);

  // Auto-refresh state
  const [lastTrackingSyncAt, setLastTrackingSyncAt] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const autoRefreshRef = useRef(null);

  const fetchTrackingOrders = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setRefreshError(null);
        const data = await getDeliveryTracking({ ...filters, page, limit: 10 });
        if (data.success) {
          setOrders(data.orders);
          setTotal(data.total);
          setPages(data.pages);
          setLastTrackingSyncAt(data.meta?.lastTrackingSyncAt);
          return data;
        }
      } catch (err) {
        console.error("Fetch Tracking Error:", err);
        if (silent) {
          setRefreshError(t("delivery_tracking.messages.auto_refresh_failed"));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [filters, page, t],
  );

  // Start a background sync and poll for progress
  const handleBulkSync = useCallback(async () => {
    if (syncJob?.status === "running" || syncJob?.status === "starting") return;
    setSyncJob({ status: "starting", processed: 0, total: 0 });
    try {
      const res = await syncTrackingStatuses(filters.providerSlug || undefined);
      if (!res.success) {
        setSyncJob(null);
        return;
      }

      const jobId = res.jobId;
      setSyncJob({ jobId, status: res.status, processed: 0, total: 0 });

      // Poll every 2 seconds
      if (syncPollRef.current) clearInterval(syncPollRef.current);
      syncPollRef.current = setInterval(async () => {
        try {
          const statusData = await getSyncJobStatus(jobId);
          setSyncJob(statusData);

          if (statusData.status === "done") {
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            // Refresh orders
            fetchTrackingOrders(true);
            // Keep 'done' status for 8 seconds to show inline message
            setTimeout(() => setSyncJob(null), 8000);
          } else if (statusData.status === "error") {
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            const msg = statusData.error || "Sync failed";
            const toastId = Date.now();
            setSyncToasts((prev) => [
              ...prev,
              { id: toastId, type: "error", message: msg },
            ]);
            setTimeout(
              () =>
                setSyncToasts((prev) => prev.filter((t) => t.id !== toastId)),
              5000,
            );
            setSyncJob(null);
          }
        } catch (err) {
          console.warn("[SyncPoll] Poll failed:", err.message);
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setSyncJob(null);
        }
      }, 2000);
    } catch (err) {
      console.warn("[handleBulkSync] Failed to start sync:", err.message);
      setSyncJob(null);
      const toastId = Date.now();
      setSyncToasts((prev) => [
        ...prev,
        {
          id: toastId,
          type: "error",
          message: "Impossible de démarrer la synchronisation",
        },
      ]);
      setTimeout(
        () => setSyncToasts((prev) => prev.filter((t) => t.id !== toastId)),
        4000,
      );
    }
  }, [filters.providerSlug, fetchTrackingOrders, syncJob]);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current);
    };
  }, []);

  // Silent fetch for auto-refresh (no UI sync trigger for auto-refresh)
  const silentRefresh = useCallback(async () => {
    fetchTrackingOrders(true);
  }, [fetchTrackingOrders]);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await getDeliveryCompanies();
        if (res.success) {
          const connected = res.data.filter((p) => p.isConnected);
          setProviders(connected);
        }
      } catch (err) {
        console.error("Error fetching providers:", err);
      }
    };
    fetchProviders();
  }, []);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await getConfirmationAgents();
        if (res.success) {
          setAgents(res.data || []);
        }
      } catch (err) {
        console.error("Error fetching agents:", err);
      }
    };
    fetchAgents();
  }, []);

  useEffect(() => {
    const checkAutoSync = async () => {
      const data = await fetchTrackingOrders();
      // Auto-sync ONCE if we have tracked orders but no sync timestamp yet
      if (data?.success && data.total > 0 && !data.meta?.lastTrackingSyncAt) {
        console.log("[AutoSync] Triggering first tracking sync...");
        try {
          await syncTrackingStatuses();
          await fetchTrackingOrders(true); // Silent refetch
        } catch (err) {
          console.warn("[AutoSync] Failed:", err.message);
        }
      }
    };
    checkAutoSync();
  }, [filters, page, fetchTrackingOrders]); // Trigger on filter/page change to catch first tracked order

  // Auto-refresh every 15 minutes
  useEffect(() => {
    autoRefreshRef.current = setInterval(() => {
      silentRefresh();
    }, 900000); // 15 minutes

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [silentRefresh]);

  const handleSync = async (id) => {
    try {
      setSyncingId(id);
      const res = await syncOrderTracking(id);
      if (res.success) {
        // Update local state
        setOrders((prev) =>
          prev.map((o) =>
            o._id === id
              ? {
                  ...o,
                  delivery: {
                    ...o.delivery,
                    status: res.status,
                    label: res.label,
                    lastSyncAt: res.lastSyncAt,
                    lastCarrierEventAt: res.lastCarrierEventAt,
                  },
                }
              : o,
          ),
        );
      }
    } catch (err) {
      console.error("Sync Error:", err);
      alert(
        err.response?.data?.message ||
          t("delivery_tracking.messages.sync_failed"),
      );
    } finally {
      setSyncingId(null);
    }
  };

  const getStatusColor = (status) => {
    const s = status?.toUpperCase() || "";
    
    // Groupings based on Sendit's visual palette
    const emerald = "bg-emerald-50 text-emerald-600 border-emerald-100"; // Delivery & Success
    const rose = "bg-rose-50 text-rose-500 border-rose-100";          // Issues & Returns
    const sky = "bg-sky-50 text-sky-600 border-sky-100";             // Logistics & Transit
    const amber = "bg-amber-50 text-amber-600 border-amber-100";       // Initial states
    const slate = "bg-slate-50 text-slate-500 border-slate-100";       // Pending/Unknown
    
    switch (s) {
      case "DELIVERED":
      case "LIVRE":
      case "DISTRIBUTED":
      case "DISTRIBUE":
        return emerald;
      case "DELIVERING":
      case "OUT_FOR_DELIVERY":
        return emerald; // In Sendit, delivery is green
      case "RETURNED":
      case "CANCELLED":
      case "REFUSED":
      case "FAILED":
      case "DELAYED":
      case "POSTPONED":
        return rose;
      case "NOTPAID":
        return rose; // Unpaid is often highlighted in reddish/pink tones in these systems
      case "IN_TRANSIT":
      case "TRANSIT":
      case "WAREHOUSE":
      case "ENTREPOT":
      case "COLIS_CREE":
      case "COLIS_MODIFIE":
        return sky;
      case "PENDING":
      case "NEW_PARCEL":
      case "TOPICKUP":
      case "RAMASSAGE_EN_COURS":
      case "PICKEDUP":
      case "RAMASSE":
        return amber;
      default:
        return slate;
    }
  };

  return (
    <div className="px-4 py-6 md:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-6">
        <div>
          <h1 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight">
            {t("delivery_tracking.title")}
          </h1>
          <p className="text-gray-400 font-medium mt-1 text-xs md:text-sm md:text-gray-500">
            {t("delivery_tracking.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          {total > 0 && (
            <div className="bg-green-50 px-2.5 py-1.5 md:px-3 md:py-2 rounded-xl border border-green-100 flex items-center gap-2">
              <Clock
                size={12}
                className="text-green-500 md:w-[14px] md:h-[14px]"
              />
              <span className="text-[9px] md:text-[10px] font-bold text-green-700 uppercase tracking-widest">
                {lastTrackingSyncAt
                  ? t("delivery_tracking.stats.updated", {
                      time: new Date(lastTrackingSyncAt).toLocaleTimeString(
                        i18n.language,
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false
                        },
                      ),
                    })
                  : "WAITING FOR FIRST SYNC"}
              </span>
            </div>
          )}
          {/* Sync & Refresh Button + Progress */}
          <div className="flex flex-col items-end gap-1.5 flex-1 md:flex-none">
            <button
              onClick={handleBulkSync}
              disabled={
                syncJob?.status === "running" || syncJob?.status === "starting"
              }
              className="bg-white px-3 py-2.5 md:px-4 md:py-2.5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-center gap-2 hover:bg-gray-50 transition-all disabled:cursor-not-allowed w-full md:w-auto"
            >
              <RefreshCw
                size={14}
                className={`text-indigo-500 shrink-0 md:w-[15px] md:h-[15px] ${
                  syncJob?.status === "running" ||
                  syncJob?.status === "starting"
                    ? "animate-spin"
                    : ""
                }`}
              />
              <span className="text-[10px] md:text-xs font-black text-gray-900 uppercase tracking-widest whitespace-nowrap">
                {syncJob?.status === "starting"
                  ? "Sync en cours..."
                  : syncJob?.status === "running"
                    ? `${syncJob.processed} / ${syncJob.total || "..."}`
                    : t("delivery_tracking.stats.sync_refresh")}
              </span>
            </button>
            {/* Progress bar */}
            {(syncJob?.status === "running" ||
              syncJob?.status === "starting") && (
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                  style={{
                    width:
                      syncJob.status === "starting"
                        ? "5%"
                        : syncJob.total > 0
                          ? `${Math.round((syncJob.processed / syncJob.total) * 100)}%`
                          : "10%",
                  }}
                />
              </div>
            )}
            {/* Completion message */}
            {syncJob?.status === "done" && (
              <span className="text-[10px] font-bold text-green-600 uppercase tracking-widest animate-in fade-in slide-in-from-top-1 duration-500">
                ✓ {syncJob.success} mis à jour, {syncJob.skipped} ignorés
              </span>
            )}
          </div>
          <div className="bg-white px-3 py-2 md:px-4 md:py-2 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-2 flex-1 md:flex-none justify-center md:justify-start">
            <TrendingUp
              size={14}
              className="text-indigo-500 md:w-[16px] md:h-[16px]"
            />
            <span className="text-[10px] md:text-xs font-black text-gray-900 uppercase tracking-widest whitespace-nowrap">
              {t("delivery_tracking.stats.total_tracked", { count: total })}
            </span>
          </div>
        </div>
      </div>

      {/* Auto-refresh Error Toast */}
      {refreshError && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 flex items-center gap-3">
          <AlertCircle size={18} className="text-amber-500" />
          <span className="text-sm font-bold text-amber-700">
            {refreshError}
          </span>
          <button
            onClick={() => setRefreshError(null)}
            className="ml-auto text-amber-400 hover:text-amber-600 text-xs font-black uppercase"
          >
            {t("common.cancel")}
          </button>
        </div>
      )}

      {/* Filters Bar */}
      <div className="bg-white p-3 md:p-6 rounded-2xl md:rounded-[2rem] border border-gray-100 shadow-sm space-y-3 md:space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-4">
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder={t("delivery_tracking.filters.placeholder")}
              className="w-full pl-11 pr-4 py-3 rounded-xl md:rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-indigo-500/20 font-bold text-xs md:text-sm"
              value={filters.search}
              onChange={(e) => {
                const val = e.target.value;
                setFilters({ ...filters, search: val });
              }}
            />
          </div>
          <select
            className="w-full px-4 py-3 rounded-xl md:rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-indigo-500/20 font-bold text-xs md:text-sm text-gray-700 appearance-none"
            value={filters.providerSlug}
            onChange={(e) =>
              setFilters({ ...filters, providerSlug: e.target.value })
            }
          >
            <option value="">
              {t("delivery_tracking.filters.all_carriers")}
            </option>
            {providers.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            className="w-full px-4 py-3 rounded-xl md:rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-indigo-500/20 font-bold text-xs md:text-sm text-gray-700 appearance-none"
            value={filters.label}
            onChange={(e) => setFilters({ ...filters, label: e.target.value })}
          >
            <option value="">
              {t("delivery_tracking.filters.all_statuses")}
            </option>
            <optgroup label="📦 Ramassage">
              <option value="Nouveau Colis">Nouveau Colis</option>
              <option value="Attente de Ramassage">Attente de Ramassage</option>
              <option value="Pré ramassé">Pré ramassé</option>
              <option value="Ramassé">Ramassé</option>
              <option value="Saisi par Barid Al-Maghrib">
                Saisi par Barid Al-Maghrib
              </option>
            </optgroup>
            <optgroup label="🚚 Transit / Expédition">
              <option value="En cours">En cours</option>
              <option value="En Voyage">En Voyage</option>
              <option value="Expédié">Expédié</option>
              <option value="Mise en Distribution">Mise en distribution</option>
              <option value="Reçu">Reçu</option>
              <option value="Reçu En Agence De Livraison">
                Reçu En Agence De Livraison
              </option>
              <option value="Envoyé à l'agence">Envoyé à l'agence</option>
              <option value="expédier par AMANA">expédier par AMANA</option>
            </optgroup>
            <optgroup label="🏠 Livraison">
              <option value="Livré">Livré</option>
              <option value="Programmé">Programmé</option>
              <option value="client intéressé">client intéressé</option>
            </optgroup>
            <optgroup label="⏳ Reporté / Retardé">
              <option value="Reporté">Reporté</option>
              <option value="Reporté (SUIVI)">Reporté (SUIVI)</option>
              <option value="reporté aujourd hui">reporté aujourd hui</option>
              <option value="Retardé">Retardé</option>
            </optgroup>
            <optgroup label="📵 Pas de réponse">
              <option value="Pas de réponse + SMS">Pas de réponse + SMS</option>
              <option value="pas réponse +déplacement">
                pas réponse +déplacement
              </option>
              <option value="pas réponse + déplacement J+2">
                pas réponse + déplacement J+2
              </option>
              <option value="pas réponse + déplacement J+3">
                pas réponse + déplacement J+3
              </option>
              <option value="Pas de réponse J+2">Pas de réponse J+2</option>
              <option value="Pas de réponse J+3">Pas de réponse J+3</option>
              <option value="Pas de reponse (SUIVI)">
                Pas de reponse (SUIVI)
              </option>
              <option value="Injoignable">Injoignable</option>
              <option value="Injoignable (SUIVI)">Injoignable (SUIVI)</option>
              <option value="Boite Vocal">Boite Vocal</option>
              <option value="Boite Vocal (SUIVI)">Boite Vocal (SUIVI)</option>
            </optgroup>
            <optgroup label="❌ Annulé / Refusé">
              <option value="Annulé">Annulé</option>
              <option value="Annulé (SUIVI)">Annulé (SUIVI)</option>
              <option value="Refusé">Refusé</option>
            </optgroup>
            <optgroup label="🔄 Retour">
              <option value="Retourné">Retourné</option>
              <option value="En retour par AMANA">En retour par AMANA</option>
              <option value="Remboursé">Remboursé</option>
            </optgroup>
            <optgroup label="⚠️ Autres">
              <option value="Erreur Numero">Erreur Numero</option>
              <option value="Hors-zone">Hors-zone</option>
              <option value="sans adresse">sans adresse</option>
            </optgroup>
          </select>
          <select
            className="w-full px-4 py-3 rounded-xl md:rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-indigo-500/20 font-bold text-xs md:text-sm text-gray-700 appearance-none"
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          >
            <option value="">Toutes les Sources</option>
            <option value="manual">📝 Manuelle</option>
            <option value="imported">📊 Importée (Sheet)</option>
          </select>
          {!isAgent && (
            <select
              className="w-full px-4 py-3 rounded-xl md:rounded-2xl bg-gray-50 border-none focus:ring-2 focus:ring-indigo-500/20 font-bold text-xs md:text-sm text-gray-700 appearance-none"
              value={filters.confirmedBy}
              onChange={(e) =>
                setFilters({ ...filters, confirmedBy: e.target.value })
              }
            >
              <option value="">Tous les Agents</option>
              {agents.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-2 bg-gray-50 px-4 py-1.5 rounded-xl md:rounded-2xl border border-transparent col-span-1 md:col-span-1">
            <Calendar size={14} className="text-gray-400" />
            <input
              type="date"
              className="bg-transparent border-none focus:ring-0 text-[11px] md:text-xs font-bold text-gray-600 w-full"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
            <span className="text-gray-300">-</span>
            <input
              type="date"
              className="bg-transparent border-none focus:ring-0 text-[11px] md:text-xs font-bold text-gray-600 w-full"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </div>
          <button
            onClick={() =>
              setFilters({
                providerSlug: "",
                label: "",
                search: "",
                from: "",
                to: "",
                source: "",
                confirmedBy: "",
              })
            }
            className="text-xs font-black text-indigo-500 uppercase tracking-widest hover:text-indigo-600 transition-colors"
          >
            {t("delivery_tracking.filters.reset")}
          </button>
        </div>
      </div>

      {/* Table / Card View */}
      <div className="bg-white rounded-2xl md:rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  {t("delivery_tracking.table.order_info")}
                </th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  {t("delivery_tracking.table.receiver")}
                </th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  {t("delivery_tracking.table.shipping_info")}
                </th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  {t("delivery_tracking.table.tracking_details")}
                </th>
                <th className="px-6 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  {t("delivery_tracking.table.status")}
                </th>
                <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">
                  {t("delivery_tracking.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-20 text-center">
                    <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
                      {t("delivery_tracking.table.scanning")}
                    </p>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-20 text-center">
                    <Package size={40} className="mx-auto mb-4 text-gray-200" />
                    <p className="text-gray-500 font-bold">
                      {t("delivery_tracking.table.empty")}
                    </p>
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order._id}
                    className={`group transition-colors ${
                      order.isPotentialDuplicate
                        ? "bg-amber-50/50 hover:bg-amber-100/50"
                        : "hover:bg-gray-50/50"
                    }`}
                  >
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors overflow-hidden border border-gray-100/50 flex-shrink-0">
                          {order.productImage ? (
                            <img
                              src={order.productImage}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.parentElement.innerHTML =
                                  '<div class="text-gray-400"><Package size={20} /></div>';
                              }}
                            />
                          ) : (
                            <Package size={20} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-black text-gray-900 tracking-tight">
                              #{order.orderId}
                            </p>
                            <DuplicateBadge order={order} />
                          </div>
                          <div className="flex flex-col gap-0.5 mt-0.5">
                            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                            {order.confirmationData?.confirmedAt && (
                              <p className="text-[9px] font-bold text-green-500 uppercase tracking-widest flex items-center gap-1">
                                <Clock size={8} />
                                CONFIRMÉ À{" "}
                                {new Date(
                                  order.confirmationData.confirmedAt,
                                ).toLocaleDateString()}{" "}
                                {new Date(
                                  order.confirmationData.confirmedAt,
                                ).toLocaleTimeString(i18n.language, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false
                                })}
                              </p>
                            )}
                            {order.confirmationData?.confirmedBy?.name && (
                              <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50/50 px-1.5 py-0.5 rounded-md w-fit">
                                BY {order.confirmationData.confirmedBy.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="flex flex-col gap-1">
                        <p className="font-bold text-gray-900 line-clamp-1">
                          {order.shipping?.receiverName ||
                            t("common.not_applicable")}
                        </p>
                        <p className="text-[10px] font-bold text-gray-400">
                          {order.shipping?.phone ||
                            t("delivery_tracking.table.no_phone")}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="flex flex-col gap-1">
                        <p className="font-bold text-gray-700 text-xs line-clamp-1">
                          {order.shipping?.cityName ||
                            t("delivery_tracking.table.no_city")}
                        </p>
                        <p
                          className="text-[10px] font-medium text-gray-400 line-clamp-1"
                          title={order.shipping?.address}
                        >
                          {order.shipping?.address ||
                            t("delivery_tracking.table.no_address")}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="flex flex-col gap-1">
                        <span className="w-fit text-[9px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100/50">
                          {order.delivery?.providerSlug ||
                            order.shipping?.providerSlug}
                        </span>
                        <p className="font-mono text-[11px] font-bold text-gray-800 break-all">
                          {order.delivery?.trackingCode ||
                            order.shipping?.trackingNumber ||
                            t("common.not_applicable")}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="space-y-1.5">
                        <div
                          className={`inline-flex px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(order.delivery?.status)}`}
                        >
                          {order.delivery?.label ||
                            (order.delivery?.status
                              ? t(
                                  `delivery_tracking.status.${order.delivery.status.toLowerCase()}`,
                                  { defaultValue: order.delivery.status },
                                )
                              : t("delivery_tracking.status.pending_sync"))}
                        </div>
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
                          <Clock size={10} />
                          {(() => {
                            const carrierDate =
                              order.delivery?.lastCarrierEventAt;
                            const historyDates = (
                              order.delivery?.statusHistory || []
                            )
                              .map((h) => (h.at ? new Date(h.at).getTime() : 0))
                              .filter((t) => !isNaN(t) && t > 0);
                            const latestHistoryDate =
                              historyDates.length > 0
                                ? Math.max(...historyDates)
                                : null;
                            const displayDate =
                              carrierDate ||
                              latestHistoryDate ||
                              order.delivery?.statusUpdatedAt;

                            return displayDate
                              ? new Date(displayDate).toLocaleString(i18n.language, { hour12: false })
                              : t(
                                  "delivery_tracking.status.unknown_carrier_time",
                                  { defaultValue: "Unknown from carrier" },
                                );
                          })()}
                        </p>
                        {order.delivery?.syncError && (
                          <div
                            className="flex items-center gap-1 text-red-500"
                            title={order.delivery.syncError}
                          >
                            <AlertCircle size={14} />
                            <span className="text-[10px] font-bold uppercase tracking-widest">
                              {t("delivery_tracking.status.sync_error")}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex items-center justify-end gap-2 text-right">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowTimeline(true);
                          }}
                          className="p-2.5 rounded-xl bg-gray-50 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all font-black text-[10px] uppercase tracking-widest flex items-center gap-2"
                          title="View History"
                        >
                          <Eye size={18} />
                          <span className="hidden lg:inline">
                            {t("delivery_tracking.table.history")}
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-gray-50">
          {loading ? (
            <div className="py-20 text-center">
              <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {t("delivery_tracking.table.scanning")}
              </p>
            </div>
          ) : orders.length === 0 ? (
            <div className="py-20 text-center px-6">
              <Package size={40} className="mx-auto mb-4 text-gray-100" />
              <p className="text-gray-400 font-bold text-sm">
                {t("delivery_tracking.table.empty")}
              </p>
            </div>
          ) : (
            orders.map((order) => (
              <div
                key={order._id}
                className={`p-4 space-y-4 ${order.isPotentialDuplicate ? "bg-amber-50/30" : ""}`}
              >
                {/* Card Header */}
                <div className="flex justify-between items-start gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100/50 overflow-hidden shrink-0">
                      {order.productImage ? (
                        <img
                          src={order.productImage}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package size={20} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-black text-gray-900 tracking-tight">
                          #{order.orderId}
                        </h3>
                        <DuplicateBadge order={order} />
                      </div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusColor(order.delivery?.status)}`}
                  >
                    {order.delivery?.label ||
                      (order.delivery?.status
                        ? t(
                            `delivery_tracking.status.${order.delivery.status.toLowerCase()}`,
                            { defaultValue: order.delivery.status },
                          )
                        : t("delivery_tracking.status.pending_sync"))}
                  </div>
                </div>

                {/* Card Body */}
                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div>
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      {t("delivery_tracking.table.receiver")}
                    </p>
                    <p className="text-xs font-bold text-gray-900 truncate">
                      {order.shipping?.receiverName || "---"}
                    </p>
                    <p className="text-[10px] font-medium text-gray-500 mt-0.5">
                      {order.shipping?.phone || "---"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                      {t("delivery_tracking.table.shipping_info")}
                    </p>
                    <p className="text-xs font-bold text-gray-700 truncate">
                      {order.shipping?.cityName || "---"}
                    </p>
                    <p className="text-[9px] font-medium text-gray-400 mt-0.5 truncate">
                      {order.shipping?.address || "---"}
                    </p>
                  </div>
                </div>

                {/* Tracking Info */}
                <div className="bg-gray-50/80 p-3 rounded-xl border border-gray-100/50 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest bg-indigo-50 px-1.5 py-0.5 rounded-md">
                      {order.delivery?.providerSlug ||
                        order.shipping?.providerSlug ||
                        "---"}
                    </span>
                    <p className="font-mono text-[10px] font-bold text-gray-600">
                      {order.delivery?.trackingCode ||
                        order.shipping?.trackingNumber ||
                        "---"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-400">
                    <Clock size={10} />
                    <span className="text-[9px] font-bold uppercase tracking-widest">
                      {(() => {
                        const displayDate =
                          order.delivery?.lastCarrierEventAt ||
                          order.delivery?.statusUpdatedAt;
                        return displayDate
                          ? new Date(displayDate).toLocaleString(i18n.language, { hour12: false })
                          : "---";
                      })()}
                    </span>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => {
                      setSelectedOrder(order);
                      setShowTimeline(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-50 text-gray-500 font-black text-[10px] uppercase tracking-widest border border-gray-100 min-h-[44px]"
                  >
                    <Eye size={16} />
                    {t("delivery_tracking.table.history")}
                  </button>
                  {isAgent && (
                    <button
                      onClick={() => handleSync(order._id)}
                      disabled={syncingId === order._id}
                      className="w-12 h-[44px] flex items-center justify-center rounded-xl bg-gray-900 text-white shadow-lg shadow-gray-200"
                    >
                      <RefreshCw
                        size={18}
                        className={
                          syncingId === order._id ? "animate-spin" : ""
                        }
                      />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pages > 1 &&
          (() => {
            const limit = 10;
            const startItem = (page - 1) * limit + 1;
            const endItem = Math.min(page * limit, total);

            // Build page numbers array with ellipsis
            const getPageNumbers = () => {
              const pageNumbers = [];
              if (pages <= 7) {
                for (let i = 1; i <= pages; i++) pageNumbers.push(i);
              } else {
                pageNumbers.push(1);
                if (page > 3) pageNumbers.push("...");
                for (
                  let i = Math.max(2, page - 1);
                  i <= Math.min(pages - 1, page + 1);
                  i++
                ) {
                  pageNumbers.push(i);
                }
                if (page < pages - 2) pageNumbers.push("...");
                pageNumbers.push(pages);
              }
              return pageNumbers;
            };

            return (
              <div className="px-4 md:px-8 py-4 md:py-6 border-t border-gray-50 flex flex-col items-center gap-3 md:gap-4">
                <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto max-w-full pb-2 md:pb-0 scrollbar-hide">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 md:p-2 rounded-lg md:rounded-xl border border-gray-100 hover:bg-gray-50 disabled:opacity-30 transition-colors shrink-0"
                  >
                    <ChevronRight
                      size={16}
                      className="rotate-180 text-gray-500 md:w-[18px] md:h-[18px]"
                    />
                  </button>
                  {getPageNumbers().map((num, idx) =>
                    num === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-1 md:px-2 text-xs md:text-sm font-bold text-gray-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={num}
                        onClick={() => setPage(num)}
                        className={`min-w-[32px] md:min-w-[36px] h-8 md:h-9 rounded-lg md:rounded-xl text-xs md:text-sm font-black transition-all shrink-0 ${
                          page === num
                            ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200"
                            : "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-100"
                        }`}
                      >
                        {num}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page === pages}
                    className="p-1.5 md:p-2 rounded-lg md:rounded-xl border border-gray-100 hover:bg-gray-50 disabled:opacity-30 transition-colors shrink-0"
                  >
                    <ChevronRight
                      size={16}
                      className="text-gray-500 md:w-[18px] md:h-[18px]"
                    />
                  </button>
                </div>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">
                  {t("delivery_tracking.pagination.range", {
                    defaultValue: `Affichage de ${startItem} - ${endItem} sur ${total} commandes`,
                    start: startItem,
                    end: endItem,
                    total: total,
                  })}
                </p>
              </div>
            );
          })()}
      </div>

      {/* Timeline Sidebar / Slider */}
      {showTimeline && selectedOrder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-end">
          <div
            className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm"
            onClick={() => setShowTimeline(false)}
          />
          <div className="bg-white h-full w-full md:max-w-lg relative z-10 shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
            <div className="p-5 md:p-8 border-b border-gray-50 flex justify-between items-center">
              <div>
                <h2 className="text-lg md:text-xl font-black text-gray-900 tracking-tight">
                  {t("delivery_tracking.timeline.title")}
                </h2>
                <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                  {t("confirmation_modal.fields.from_order")} #
                  {selectedOrder.orderId}
                </p>
              </div>
              <button
                onClick={() => setShowTimeline(false)}
                className="p-2 md:p-2.5 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XCircle
                  size={20}
                  className="text-gray-300 hover:text-indigo-500 md:w-[24px] md:h-[24px]"
                />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 md:p-8">
              <TrackingTimeline
                history={selectedOrder.delivery?.statusHistory}
                currentStatus={selectedOrder.delivery?.status}
              />

              {/* Additional Shipping Details as requested */}
              <div className="mt-8 border-t border-gray-50 pt-8 space-y-4">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">
                  {t("delivery_tracking.timeline.snapshot")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-2xl">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tight mb-1">
                      {t("delivery_tracking.timeline.price")}
                    </p>
                    <p className="text-xs font-bold text-gray-700">
                      {selectedOrder.shipping?.price?.toLocaleString() || 0}{" "}
                      {t("common.currency")}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-2xl">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tight mb-1">
                      {t("delivery_tracking.timeline.variant")}
                    </p>
                    <p className="text-xs font-bold text-gray-700">
                      <bdi>
                        {typeof selectedOrder.shipping?.variant === 'object'
                          ? [selectedOrder.shipping.variant.color, formatSize(selectedOrder.shipping.variant.size)].filter(Boolean).join(" - ")
                          : (formatSize(selectedOrder.shipping?.variant) || t("orders.table.standard_variant"))}
                      </bdi>
                    </p>
                  </div>
                </div>
                {selectedOrder.shipping?.note && (
                  <div className="bg-amber-50/50 p-4 rounded-2xl border border-amber-100/50">
                    <p className="text-[9px] font-black text-amber-500 uppercase tracking-tight mb-1">
                      {t("delivery_tracking.timeline.note")}
                    </p>
                    <p className="text-xs font-bold text-amber-700 italic">
                      "{selectedOrder.shipping.note}"
                    </p>
                  </div>
                )}

                {/* Courier Details */}
                <div className="mt-8 border-t border-gray-50 pt-8">
                  <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">
                    {t("delivery_tracking.timeline.courier_info")}
                  </h3>
                  {selectedOrder.delivery?.courier?.name ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100/50">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-indigo-500 shadow-sm border border-indigo-50">
                          <Truck size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">
                            {t("delivery_tracking.timeline.driver")}
                          </p>
                          <p className="text-sm font-black text-gray-900">
                            {selectedOrder.delivery.courier.name}
                          </p>
                          {selectedOrder.delivery.courier.vehicle && (
                            <p className="text-[10px] font-bold text-gray-400 mt-0.5">
                              {selectedOrder.delivery.courier.vehicle}
                            </p>
                          )}
                        </div>
                        {selectedOrder.delivery.courier.phone && (
                          <a
                            href={`tel:${selectedOrder.delivery.courier.phone}`}
                            className="p-2.5 rounded-xl bg-indigo-500 text-white hover:bg-indigo-600 transition-colors shadow-lg shadow-indigo-200"
                          >
                            <Phone size={18} />
                          </a>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50/50 p-4 rounded-2xl border border-gray-100/50 flex items-center justify-center text-gray-400 border-dashed">
                      <p className="text-[10px] font-black uppercase tracking-widest">
                        {t("delivery_tracking.timeline.no_courier_info")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Sync Progress Toasts */}
      <div className="fixed top-8 right-8 z-[110] flex flex-col gap-4">
        {syncToasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-4 px-6 py-5 rounded-[2rem] shadow-2xl border animate-in slide-in-from-right-full duration-500 min-w-[320px] ${
              toast.type === "success"
                ? "bg-white border-green-100 text-gray-900"
                : "bg-white border-red-100 text-gray-900"
            }`}
          >
            {toast.type === "success" ? (
              <div className="w-10 h-10 rounded-2xl bg-green-50 flex items-center justify-center text-green-500">
                <CheckCircle2 size={24} />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center text-red-500">
                <XCircle size={24} />
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-0.5">
                {toast.type === "success"
                  ? "Synchronisation réussie"
                  : "Erreur de sync"}
              </span>
              <span className="font-bold text-sm tracking-tight text-gray-800">
                {toast.message}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeliveryTracking;
